import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

interface FormatData {
  weight?: {
    airmailNet?: number;
    airmailGross?: number;
    surfaceNet?: number;
    surfaceGross?: number;
    salNet?: number;
    salGross?: number;
    tdRate?: number;
    totalPriority?: number;
    totalNonPriority?: number;
    terminalDues?: number;
    airmailNetFormatted?: string;
    airmailGrossFormatted?: string;
    surfaceNetFormatted?: string;
    surfaceGrossFormatted?: string;
    salNetFormatted?: string;
    salGrossFormatted?: string;
    totalPriorityFormatted?: string;
    totalNonPriorityFormatted?: string;
    tdRateFormatted?: string;
    terminalDuesFormatted?: string;
  };
  items?: {
    airmailNet?: number;
    airmailGross?: number;
    surfaceNet?: number;
    surfaceGross?: number;
    salNet?: number;
    salGross?: number;
    tdRate?: number;
    totalPriority?: number;
    totalNonPriority?: number;
    terminalDues?: number;
    airmailNetFormatted?: string;
    airmailGrossFormatted?: string;
    surfaceNetFormatted?: string;
    surfaceGrossFormatted?: string;
    salNetFormatted?: string;
    salGrossFormatted?: string;
    totalPriorityFormatted?: string;
    totalNonPriorityFormatted?: string;
    tdRateFormatted?: string;
    terminalDuesFormatted?: string;
  };
}

interface PriorityData {
  formatP?: FormatData;
  formatG?: FormatData;
  formatE?: FormatData;
  postcards?: FormatData;
  registered?: { items?: any };
  insured?: { items?: any };
  express?: { items?: any };
  totalPriority?: {
    terminalDuesTotal?: number;
    terminalDuesTotalFormatted?: string;
  };
  mbags?: FormatData;
}

interface NonPriorityData {
  formatP?: FormatData;
  formatG?: FormatData;
  formatE?: FormatData;
  postcards?: FormatData;
  registered?: { items?: any };
  insured?: { items?: any };
  express?: { items?: any };
  totalNonPriority?: {
    terminalDuesTotal?: number;
    terminalDuesTotalFormatted?: string;
  };
  mbags?: FormatData;
}

interface CN61HtmlReportData {
  currency: string;
  priorityTd?: PriorityData;
  nonPriorityTd?: NonPriorityData;
  priorityAndNonPriorityTdTotals?: {
    totalTerminalDues?: number;
    totalTerminalDuesFormatted?: string;
  };
}

interface CN61HtmlReportTableProps {
  data: CN61HtmlReportData[];
  title?: string;
  direction?: 'inbound' | 'outbound' | 'unknown';
}

function CN61HtmlReportTable({ data, title, direction = 'unknown' }: CN61HtmlReportTableProps): React.ReactElement {
  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data available
        </Typography>
      </Box>
    );
  }

  const reportData = data[0]; // Take the first item from the array

  // Background colors based on direction
  const getBackgroundColor = (isHeader: boolean = false) => {
    if (isHeader) {
      return direction === 'inbound' ? '#e8f5e8' : direction === 'outbound' ? '#ffebee' : '#f5f5f5';
    } else {
      return direction === 'inbound' ? '#f0f8f0' : direction === 'outbound' ? '#fff0f0' : '#fafafa';
    }
  };

  const renderPriorityTable = (priorityData: PriorityData) => {
    const formatSections = [
      { key: 'formatP', label: 'Format P', data: priorityData.formatP },
      { key: 'formatG', label: 'Format G', data: priorityData.formatG },
      { key: 'formatE', label: 'Format E', data: priorityData.formatE },
      { key: 'postcards', label: 'Postcards', data: priorityData.postcards },
    ];

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 2 }}>
          Priority Mail
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              {/* Parent column headers */}
              <TableRow sx={{ backgroundColor: getBackgroundColor(true) }}>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: 120, 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Format
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Type
                </TableCell>
                <TableCell 
                  colSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  A: Airmail Priority
                </TableCell>
                <TableCell 
                  colSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  D: Surface Priority
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Total Priority
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  TD Rate [{reportData.currency || 'EUR'}]
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle'
                  }}
                >
                  Terminal Dues [{reportData.currency || 'EUR'}]
                </TableCell>
              </TableRow>
              {/* Child column headers */}
              <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Net
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Gross
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Net
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Gross
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formatSections.map((section) => {
                if (!section.data) return null;
                
                const rows = [];
                
                // Weight row with merged format cell
                if (section.data.weight) {
                  rows.push(
                    <TableRow key={`${section.key}-weight`} sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell 
                        rowSpan={section.data.items ? 2 : 1}
                        sx={{ 
                          fontWeight: 500, 
                          borderRight: '1px solid #ddd', 
                          backgroundColor: getBackgroundColor(false),
                          verticalAlign: 'middle'
                        }}
                      >
                        {section.label}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Weight</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.airmailNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.airmailGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.surfaceNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.surfaceGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.totalPriorityFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  );
                }
                
                // Items row (without format cell since it's merged with weight row)
                if (section.data.items) {
                  rows.push(
                    <TableRow key={`${section.key}-items`} sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.airmailNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.airmailGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.surfaceNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.surfaceGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.totalPriorityFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  );
                }
                
                return rows;
              })}
              
              {/* Registered items */}
              {priorityData.registered?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Registered</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.airmailNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.airmailGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.totalPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.registered.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Insured items */}
              {priorityData.insured?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Insured</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.airmailNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.airmailGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.totalPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.insured.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Express items */}
              {priorityData.express?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Express</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.airmailNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.airmailGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.totalPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.express.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* M-Bags */}
              {priorityData.mbags && (
                <>
                  {priorityData.mbags.weight && (
                    <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell 
                        rowSpan={priorityData.mbags.items ? 2 : 1}
                        sx={{ 
                          fontWeight: 500, 
                          borderRight: '1px solid #ddd', 
                          backgroundColor: getBackgroundColor(false),
                          verticalAlign: 'middle'
                        }}
                      >
                        M-Bags
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Weight</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.airmailNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.airmailGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.surfaceNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.surfaceGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.totalPriorityFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.weight.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}
                  {priorityData.mbags.items && (
                    <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.airmailNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.airmailGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.surfaceNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.surfaceGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.totalPriorityFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {priorityData.mbags.items.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              
              {/* Total row */}
              {priorityData.totalPriority && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell colSpan={8} sx={{ fontWeight: 700, textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    Total Priority Terminal Dues:
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', backgroundColor: getBackgroundColor(false) }}>
                    {priorityData.totalPriority.terminalDuesTotalFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderNonPriorityTable = (nonPriorityData: NonPriorityData) => {
    const formatSections = [
      { key: 'formatP', label: 'Format P', data: nonPriorityData.formatP },
      { key: 'formatG', label: 'Format G', data: nonPriorityData.formatG },
      { key: 'formatE', label: 'Format E', data: nonPriorityData.formatE },
      { key: 'postcards', label: 'Postcards', data: nonPriorityData.postcards },
    ];

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 2 }}>
          Non-Priority Mail
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              {/* Parent column headers */}
              <TableRow sx={{ backgroundColor: getBackgroundColor(true) }}>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: 120, 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Format
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Type
                </TableCell>
                <TableCell 
                  colSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  B: SAL
                </TableCell>
                <TableCell 
                  colSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  C: Surface Non-Priority
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  Total Non-Priority
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle',
                    borderRight: '1px solid #ddd'
                  }}
                >
                  TD Rate [{reportData.currency || 'EUR'}]
                </TableCell>
                <TableCell 
                  rowSpan={2} 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center', 
                    verticalAlign: 'middle'
                  }}
                >
                  Terminal Dues [{reportData.currency || 'EUR'}]
                </TableCell>
              </TableRow>
              {/* Child column headers */}
              <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Net
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Gross
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Net
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', borderRight: '1px solid #ddd' }}>
                  Accounting Type Gross
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formatSections.map((section) => {
                if (!section.data) return null;
                
                const rows = [];
                
                // Weight row with merged format cell
                if (section.data.weight) {
                  rows.push(
                    <TableRow key={`${section.key}-weight`} sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell 
                        rowSpan={section.data.items ? 2 : 1}
                        sx={{ 
                          fontWeight: 500, 
                          borderRight: '1px solid #ddd', 
                          backgroundColor: getBackgroundColor(false),
                          verticalAlign: 'middle'
                        }}
                      >
                        {section.label}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Weight</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.salNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.salGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.surfaceNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.surfaceGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.totalNonPriorityFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {section.data.weight.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  );
                }
                
                // Items row (without format cell since it's merged with weight row)
                if (section.data.items) {
                  rows.push(
                    <TableRow key={`${section.key}-items`} sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.salNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.salGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.surfaceNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.surfaceGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.totalNonPriorityFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {section.data.items.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  );
                }
                
                return rows;
              })}
              
              {/* Registered items */}
              {nonPriorityData.registered?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Registered</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.salNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.salGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.totalNonPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.registered.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Insured items */}
              {nonPriorityData.insured?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Insured</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.salNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.salGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.totalNonPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.insured.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* Express items */}
              {nonPriorityData.express?.items && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Express</TableCell>
                  <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.salNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.salGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.surfaceNetFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.surfaceGrossFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.totalNonPriorityFormatted || '0'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.tdRateFormatted || '1.000'}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.express.items.terminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
              
              {/* M-Bags */}
              {nonPriorityData.mbags && (
                <>
                  {nonPriorityData.mbags.weight && (
                    <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell 
                        rowSpan={nonPriorityData.mbags.items ? 2 : 1}
                        sx={{ 
                          fontWeight: 500, 
                          borderRight: '1px solid #ddd', 
                          backgroundColor: getBackgroundColor(false),
                          verticalAlign: 'middle'
                        }}
                      >
                        M-Bags
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Weight</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.salNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.salGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.surfaceNetFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.surfaceGrossFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.totalNonPriorityFormatted || '0.0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.weight.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}
                  {nonPriorityData.mbags.items && (
                    <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                      <TableCell sx={{ textAlign: 'center', fontStyle: 'italic', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>Items</TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.salNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.salGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.surfaceNetFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.surfaceGrossFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.totalNonPriorityFormatted || '0'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.tdRateFormatted || '1.000'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600, backgroundColor: getBackgroundColor(false) }}>
                        {nonPriorityData.mbags.items.terminalDuesFormatted || '0.00'}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              
              {/* Total row */}
              {nonPriorityData.totalNonPriority && (
                <TableRow sx={{ backgroundColor: getBackgroundColor(false) }}>
                  <TableCell colSpan={8} sx={{ fontWeight: 700, textAlign: 'right', borderRight: '1px solid #ddd', backgroundColor: getBackgroundColor(false) }}>
                    Total Non-Priority Terminal Dues:
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: '#1976d2', backgroundColor: getBackgroundColor(false) }}>
                    {nonPriorityData.totalNonPriority.terminalDuesTotalFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }} data-testid="cn61-html-report-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 3 }} data-testid="cn61-html-report-header">
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            color: direction === 'inbound' ? '#2e7d32' : direction === 'outbound' ? '#c62828' : '#063142',
            mb: 1,
            p: 2,
            backgroundColor: getBackgroundColor(true),
            borderRadius: 1,
            textAlign: 'center'
          }} 
          data-testid="cn61-html-report-title"
        >
          {title}
        </Typography>
      </Box>

      {/* Priority Table */}
      {reportData.priorityTd && renderPriorityTable(reportData.priorityTd)}

      {/* Non-Priority Table */}
      {reportData.nonPriorityTd && renderNonPriorityTable(reportData.nonPriorityTd)}

      {/* Overall Total */}
      {reportData.priorityAndNonPriorityTdTotals && (
        <Box sx={{ mt: 3 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: getBackgroundColor(true) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: '#1976d2', backgroundColor: getBackgroundColor(true) }}>
                    Grand Total Terminal Dues
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, fontSize: '1rem', color: 'black', backgroundColor: getBackgroundColor(true) }}>
                    {reportData.priorityAndNonPriorityTdTotals.totalTerminalDuesFormatted || '0.00'}
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </Box>
      )}

    </Box>
  );
}

export default CN61HtmlReportTable;
